import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, Skeleton } from "antd";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { actions as routerActions } from "redux-router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import ModalFooter from "../../common/components/ModalFooter";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { routeNames } from "../common/routes.ts";
import { materialsActions } from "../../features/materials/materialsSlice";
import type { Materials } from "../generated-axios-client/index.ts";
import { MaterialFields } from "./MaterialFields";

type NewMaterialPageProps = {
	dispatch: Dispatch;
	intl: IntlShape;
	initialValue?: Materials | null;
};

export function NewMaterialPage({
	dispatch,
	intl,
	initialValue,
}: NewMaterialPageProps) {
	const [form] = Form.useForm();

	const isEdit = !!initialValue?.id;

	const save = () => {
		form
			.validateFields()
			.then((values) => {
				const payload = isEdit ? { ...values, id: initialValue.id } : values;
				dispatch(materialsActions.addOrUpdateMaterial(payload));
			})
			.catch((errorInfo) => {
				console.error("Validation failed:", errorInfo);
			});
	};

	const initialValues = useMemo(
		() => (initialValue ? { ...initialValue } : undefined),
		[initialValue],
	);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	if (isEdit && !initialValue) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({
					id: isEdit ? "material_edit_title" : "material_create_title",
				})}
				subtitle={
					isEdit
						? undefined
						: intl.formatMessage({ id: "material_create_subtitle" })
				}
			>
				<Form form={form} layout="vertical" initialValues={initialValues}>
					<MaterialFields intl={intl} />
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(
								routerActions.navigateTo(routeNames.SETTINGS_MATERIAL_LIST),
							)
						}
					>
						{intl.formatMessage({ id: "ticket_button_title_cancel" })}
					</Button>
					<Button type="primary" onClick={save}>
						{intl.formatMessage({ id: "ticket_button_title_create" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const ConnectedNewMaterialPage = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	initialValue: state.materials.material,
}))(NewMaterialPage);

export default ConnectedNewMaterialPage;
